import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';
import styles from './AARForm.module.css';
import { ArrowLeft, Save, Send, Search, UserPlus, X, RefreshCw } from 'lucide-react';

interface EventAttendance {
  id?: number;
  memberId: number;
  username: string;
  rsvpStatus?: string;
  actuallyAttended: boolean;
  pointsAwarded: number;
}

interface AARData {
  id?: number;
  eventId: number;
  eventTitle: string;
  eventStartDate: string;
  eventEndDate: string;
  reportTitle: string;
  reportContent: string;
  eventPointsAwarded: number;
  status: string;
  attendances: EventAttendance[];
}

interface SearchResult {
  id: number;
  username: string;
  discordDisplayName?: string;
}

const AARForm: React.FC = () => {
  const navigate = useNavigate();
  const { eventId, aarId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aarData, setAARData] = useState<AARData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (aarId) {
      fetchAAR();
    } else if (eventId) {
      initializeNewAAR();
    }
  }, [aarId, eventId]);

  const fetchAAR = async () => {
    try {
      const response = await axios.get(`/afteractionreports/${aarId}`);
      setAARData(response.data);
    } catch (error) {
      console.error('Error fetching AAR:', error);
      alert('Failed to load After Action Report');
      navigate('/after-action-reports');
    } finally {
      setLoading(false);
    }
  };

  const initializeNewAAR = async () => {
    try {
      const response = await axios.post('/afteractionreports', {
        eventId: parseInt(eventId!),
        reportTitle: 'After Action Report',
        eventPointsAwarded: 50
      });
      setAARData(response.data);
    } catch (error: any) {
      console.error('Error creating AAR:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create After Action Report';
      alert(`Failed to create AAR: ${errorMsg}`);
      navigate('/after-action-reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`/auth/user/search?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching members:', error);
    }
  };

  const handleAddWalkIn = (member: SearchResult) => {
    if (!aarData) return;

    // Check if member is already in the list
    if (aarData.attendances.some(a => a.memberId === member.id)) {
      alert('This member is already in the attendance list');
      return;
    }

    setAARData({
      ...aarData,
      attendances: [
        ...aarData.attendances,
        {
          memberId: member.id,
          username: member.username,
          rsvpStatus: undefined,
          actuallyAttended: true,
          pointsAwarded: 0
        }
      ]
    });

    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  const toggleAttendance = (index: number) => {
    if (!aarData) return;

    const updated = [...aarData.attendances];
    updated[index].actuallyAttended = !updated[index].actuallyAttended;
    setAARData({ ...aarData, attendances: updated });
  };

  const handleSaveDraft = async () => {
    if (!aarData) return;

    setSaving(true);
    try {
      await axios.put(`/afteractionreports/${aarData.id}`, {
        reportTitle: aarData.reportTitle,
        reportContent: aarData.reportContent,
        eventPointsAwarded: aarData.eventPointsAwarded,
        attendances: aarData.attendances
      });
      alert('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshAttendances = async () => {
    if (!aarData) return;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Refresh attendances from event RSVPs? This will replace the current attendance list with everyone who RSVP\'d to the event.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`/afteractionreports/${aarData.id}/refresh-attendances`);
      setAARData(response.data);
      alert('Attendances refreshed from RSVPs');
    } catch (error) {
      console.error('Error refreshing attendances:', error);
      alert('Failed to refresh attendances');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!aarData) return;

    const attendeeCount = aarData.attendances.filter(a => a.actuallyAttended).length;
    const totalPoints = attendeeCount * aarData.eventPointsAwarded;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Finalize this report?\n\n${attendeeCount} attendees will receive ${aarData.eventPointsAwarded} points each (${totalPoints} total).\n\nThis action cannot be undone and the event will be archived.`)) {
      return;
    }

    setSaving(true);
    try {
      // Save latest changes first
      await axios.put(`/afteractionreports/${aarData.id}`, {
        reportTitle: aarData.reportTitle,
        reportContent: aarData.reportContent,
        eventPointsAwarded: aarData.eventPointsAwarded,
        attendances: aarData.attendances
      });

      // Then finalize
      await axios.post(`/afteractionreports/${aarData.id}/finalize`);
      alert('Report finalized and points awarded!');
      navigate('/after-action-reports');
    } catch (error) {
      console.error('Error finalizing AAR:', error);
      alert('Failed to finalize report');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!aarData) {
    return null;
  }

  const attendeeCount = aarData.attendances.filter(a => a.actuallyAttended).length;
  const isFinalized = aarData.status === 'Finalized';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/after-action-reports')}>
          <ArrowLeft size={20} />
          Back to Reports
        </button>
        <div className={styles.headerActions}>
          {!isFinalized && (
            <>
              <button
                className={styles.btnSecondary}
                onClick={handleSaveDraft}
                disabled={saving}
              >
                <Save size={18} />
                Save Draft
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleFinalize}
                disabled={saving}
              >
                <Send size={18} />
                Finalize Report
              </button>
            </>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Event Details</h2>
          <div className={styles.eventInfo}>
            <div className={styles.infoItem}>
              <strong>Event:</strong>
              <span>{aarData.eventTitle}</span>
            </div>
            <div className={styles.infoItem}>
              <strong>Date:</strong>
              <span>{new Date(aarData.eventStartDate).toLocaleDateString()}</span>
            </div>
            {isFinalized && (
              <div className={styles.statusBadge}>
                Report Finalized
              </div>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Report Title</h2>
          <input
            type="text"
            className={styles.input}
            value={aarData.reportTitle}
            onChange={(e) => setAARData({ ...aarData, reportTitle: e.target.value })}
            disabled={isFinalized}
            placeholder="Enter report title..."
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Report Content</h2>
          <textarea
            className={styles.textarea}
            value={aarData.reportContent}
            onChange={(e) => setAARData({ ...aarData, reportContent: e.target.value })}
            disabled={isFinalized}
            placeholder="Describe the event, outcomes, notable moments, lessons learned..."
            rows={10}
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Points per Attendee</h2>
          <input
            type="number"
            className={styles.input}
            value={aarData.eventPointsAwarded}
            onChange={(e) => setAARData({ ...aarData, eventPointsAwarded: parseInt(e.target.value) || 0 })}
            disabled={isFinalized}
            min="0"
            step="10"
          />
          <p className={styles.helpText}>
            Total points to award: {attendeeCount} × {aarData.eventPointsAwarded} = {attendeeCount * aarData.eventPointsAwarded}
          </p>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Attendance ({attendeeCount} confirmed)</h2>
            {!isFinalized && !showSearch && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className={styles.btnSecondary}
                  onClick={handleRefreshAttendances}
                  title="Re-sync attendances from event RSVPs"
                >
                  <RefreshCw size={18} />
                  Refresh from RSVPs
                </button>
                <button
                  className={styles.btnAdd}
                  onClick={() => setShowSearch(true)}
                >
                  <UserPlus size={18} />
                  Add Walk-in
                </button>
              </div>
            )}
          </div>

          {showSearch && (
            <div className={styles.searchBox}>
              <div className={styles.searchInputWrapper}>
                <Search className={styles.searchIcon} size={18} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search for member..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  autoFocus
                />
                <button
                  className={styles.searchClose}
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className={styles.searchResults}>
                  {searchResults.map(member => (
                    <button
                      key={member.id}
                      className={styles.searchResult}
                      onClick={() => handleAddWalkIn(member)}
                    >
                      <span>{member.username}</span>
                      {member.discordDisplayName && (
                        <span className={styles.displayName}>({member.discordDisplayName})</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={styles.attendanceList}>
            {aarData.attendances.map((attendance, index) => (
              <div key={index} className={styles.attendanceItem}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={attendance.actuallyAttended}
                    onChange={() => toggleAttendance(index)}
                    disabled={isFinalized}
                  />
                  <span className={styles.checkboxLabel}>
                    {attendance.username}
                    {attendance.rsvpStatus && (
                      <span className={styles.rsvpBadge}>RSVP: {attendance.rsvpStatus}</span>
                    )}
                    {!attendance.rsvpStatus && (
                      <span className={styles.walkInBadge}>Walk-in</span>
                    )}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AARForm;
